import { debounce, isEqual, pick, throttle } from "lodash";
import type React from "react";
import {
  Fragment,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { cn } from "@/lib/utils";
import { useAutoUpdateRef } from "@/utils/utils";
import {
  calculateViewportVisibility,
  computeCenteredViewport,
  MAX_ZOOM,
  MIN_ZOOM,
} from "../Canvas/hooks/useCanvasViewportControl";
import { CONTROLS_MAX_HEIGHT } from "../Canvas/Storyboard/StoryboardControlsOverlay";
import { ClipBounds } from "./ClipBounds";
import ResizableOutline, { type ItemRect } from "./ResizableOutline";
import { useSimpleCanvasStore } from "./simpleCanvasStore";

// ================================================================================
// CONSTANTS
// ================================================================================

// Element drag-out functionality
export const TEMP_ELEMENT_DRAG_STORYBOARD_ID = "temp-element-drag";

// Zoom sensitivity values - fine-tuned for natural feel
const TRACKPAD_ZOOM_SENSITIVITY = 0.012; // For trackpad pinch gestures (responsive, natural feel)
const MOUSE_WHEEL_ZOOM_SENSITIVITY = 0.001; // For mouse wheel (gentler, stepped feel)
const META_ZOOM_MULTIPLIER = 0.6; // 60% the speed in either direction
const CALLBACK_THROTTLE_FPS = 24;

// Zoom start
export const START_MIN_ZOOM = 0.15;

// Thresholds for gesture detection
const GESTURE_TIMEOUT_MS = 100; // Time to consider events part of same gesture

// ================================================================================
// TYPES & INTERFACES
// ================================================================================

type CssPropertiesWithVars = React.CSSProperties & {
  [key: `--${string}`]: string | number;
};

export interface CanvasItem {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  hidden?: boolean;
  pending?: boolean;
}

interface DragState {
  isDragging: boolean;
  dragId: string | null;
  startX: number;
  startY: number;
  offsetX: number;
  offsetY: number;
  originalX: number;
  originalY: number;
  originalPositions: Map<string, { x: number; y: number }>;
}

interface SelectionState {
  selectedIds: Set<string>;
  isDragSelecting: boolean;
  dragStartX: number;
  dragStartY: number;
  dragEndX: number;
  dragEndY: number;
}

export interface SimpleCanvasRef {
  onItemWheel: (wheelEvent: WheelEvent, itemId: string) => void;
  setZoom: (zoom: number) => void;
  getZoom: () => number;
  getTransform: () => { x: number; y: number; zoom: number };
  getCanvasRef: () => HTMLDivElement | null;
  itemToCanvas: (
    item: CanvasItem,
    coords: { x: number; y: number },
  ) => { canvasX: number; canvasY: number };
  setFocusedId: (id: string | null) => void;
  setSelectedIds: (ids: Set<string>) => void;
  centerViewOnItems: (
    ids: string[],
    bufferFactor?: number,
    animate?: boolean,
    skipZoom?: boolean,
    minZoom?: number,
  ) => void;
  setCanvasTransform: (transform: {
    x: number;
    y: number;
    zoom: number;
  }) => void;
  getItemsAtCoordinates: (
    canvasX: number,
    canvasY: number,
    ignoreItemIds?: string[],
    includeApproximatedControls?: boolean,
  ) => {
    topItem?: CanvasItem | null;
    items?: CanvasItem[];
  };
  isCanvasAtClientCoordinates: (clientX: number, clientY: number) => boolean;
  startDragItem: (
    item: CanvasItem,
    coords: { canvasX: number; canvasY: number },
  ) => void;
  clientToCanvas: (
    clientX: number,
    clientY: number,
  ) => { x: number; y: number };
  overlayToCanvas: (x: number, y: number) => { x: number; y: number };
  getIsDragging: () => boolean;
}

export type CanvasItemState = {
  isSelected: boolean;
  isFocused: boolean;
  isPanning: boolean;
  isZooming: boolean;
  isResizing: boolean;
  isInteractable: boolean;
  isVisible: boolean;

  // Drag/drop state
  canDropThisItem: boolean;
  draggingThisItem: boolean;
  numDraggingItems: number;
};

// Generic ref type for canvas items - items can expose any methods they want
export type CanvasItemRef = {
  onResizeInstantUpdate?: (rect: ItemRect) => void;
  onResizeEnd?: () => void;
  // Add more methods as needed
};

// TODO: Make this more of a CanvasState type
export type CanvasState = {
  isDragging: boolean;
  isZooming: boolean;
  isPanning: boolean;
  isDragSelecting: boolean;
};

interface SimpleCanvasProps<T extends CanvasItem> {
  items: T[];
  children?: React.ReactNode;
  onItemsChange?: (items: T[], endOfAction: boolean) => void;
  renderItem: (
    item: T,
    itemState: CanvasItemState,
    registerRef: (ref: CanvasItemRef | null) => void,
  ) => React.ReactNode;
  renderAnnotations?: (items: T[], canvasState: CanvasState) => React.ReactNode;
  onSelectionChange?: (selectedIds: Set<string>) => void;
  onZoom?: (zoom: number) => void;
  onTransformChange?: (
    transform: { x: number; y: number; zoom: number },
    pageKey?: string,
  ) => void;
  pageKey?: string;
  initialViewport?: { x: number; y: number; zoom: number }; // Used every time a new page is shown
  onMouseMove?: (e: {
    clientX: number;
    clientY: number;
    canvasX: number;
    canvasY: number;
    draggingItemId?: string;
  }) => void;
  onMouseDown?: (e: {
    clientX: number;
    clientY: number;
    canvasX: number;
    canvasY: number;
    itemId?: string;
    item?: T;
  }) => void;
  onMouseUp?: (e: React.MouseEvent<Element, MouseEvent> | MouseEvent) => void;
  onMouseEnter?: (
    e: React.MouseEvent<Element, MouseEvent>,
    itemId: string,
  ) => void;
  onMouseLeave?: (
    e: React.MouseEvent<Element, MouseEvent>,
    itemId: string,
  ) => void;
  className?: string;
  onDrawRectStart?: (pos: { x: number; y: number }) => T | boolean; // boolean for if its handled or not
  onDrawRectEnd?: (item: T) => void;
  onContextMenu?: (
    pos: {
      clientX: number;
      clientY: number;
    },
    itemId?: string,
  ) => void;
  onDrop?: (e: React.DragEvent<Element>) => void;
  onDropItemIntoOtherItem?: (
    itemId: string,
    itemDroppingIntoId: string,
    dropOffsetX: number,
    dropOffsetY: number,
  ) => void;
  onDropItemOntoCanvas?: (itemId: string) => void;
  onCancelDragItem?: () => void;
  forcePanOnClick?: boolean;
  forceInteractableIfMouseOver?: boolean;
  forceItemIdsInteractable?: Set<string>;
  preventAllActions?: boolean;
  preventSelect?: boolean;
  canDropDraggingItems?: boolean;
}

// ================================================================================
// HELPER HOOKS
// ================================================================================

const useCanvasCoordinateHelpers = (
  canvasRef: React.RefObject<HTMLDivElement>,
  transformRef: React.RefObject<{ x: number; y: number; zoom: number }>,
  itemsRef: React.RefObject<CanvasItem[]>,
  itemElementsRef: React.RefObject<Map<string, HTMLDivElement>>,
) => {
  const canvasToOverlay = useCallback(
    (canvasX: number, canvasY: number) => {
      const transform = transformRef.current;
      if (!transform) return { x: canvasX, y: canvasY };
      return {
        x: canvasX * transform.zoom + transform.x,
        y: canvasY * transform.zoom + transform.y,
      };
    },
    [transformRef],
  );

  const overlayToCanvas = useCallback(
    (overlayX: number, overlayY: number) => {
      const transform = transformRef.current;
      if (!transform) return { x: overlayX, y: overlayY };
      return {
        x: (overlayX - transform.x) / transform.zoom,
        y: (overlayY - transform.y) / transform.zoom,
      };
    },
    [transformRef],
  );

  const itemToCanvas = useCallback(
    (item: CanvasItem, coords: { x: number; y: number }) => {
      // Convert coords to percentages within the item
      const xPercent = coords.x / item.width;
      const yPercent = coords.y / item.height;

      // Project onto the item's rendered position and dimensions in canvas
      const canvasX = item.x + xPercent * item.width;
      const canvasY = item.y + yPercent * item.height;

      return { canvasX, canvasY };
    },
    [],
  );

  /**
   * Returns items from top z index to bottom z index
   */
  const getItemsAtCoordinates = useCallback(
    (
      canvasX: number,
      canvasY: number,
      ignoreItemIds?: string[],
      includeApproximatedControls?: boolean,
    ): {
      topItem?: CanvasItem | null;
      items?: CanvasItem[];
    } => {
      const canvasElement = canvasRef.current;
      if (!canvasElement) return {};

      // Find all items that contain the given coordinates
      const itemsAtCoordinates = itemsRef.current
        ?.filter((item) => {
          return (
            canvasX >= item.x &&
            canvasX <= item.x + item.width &&
            canvasY >=
              item.y -
                (includeApproximatedControls ? CONTROLS_MAX_HEIGHT : 0) &&
            canvasY <= item.y + item.height &&
            !item.hidden &&
            !ignoreItemIds?.includes(item.id)
          );
        })
        .sort((a, b) => {
          const elementA = itemElementsRef.current?.get(a.id);
          const elementB = itemElementsRef.current?.get(b.id);
          const zIndexA = elementA ? parseInt(elementA.style.zIndex || "1") : 1;
          const zIndexB = elementB ? parseInt(elementB.style.zIndex || "1") : 1;
          return zIndexB - zIndexA;
        });

      // Return the item with the highest z-index (last in the array has highest z-index)
      return {
        topItem: itemsAtCoordinates?.[itemsAtCoordinates.length - 1],
        items: itemsAtCoordinates,
      };
    },
    [],
  );

  const isCanvasAtClientCoordinates = useCallback(
    (clientX: number, clientY: number) => {
      const canvasElement = canvasRef.current;
      if (!canvasElement) return false;
      const rect = canvasElement.getBoundingClientRect();
      return (
        clientX >= rect.left &&
        clientX <= rect.right &&
        clientY >= rect.top &&
        clientY <= rect.bottom
      );
    },
    [canvasRef],
  );

  const clientToCanvas = useCallback(
    (screenX: number, screenY: number) => {
      const canvasElement = canvasRef.current;
      if (!canvasElement) return { x: 0, y: 0 };

      const rect = canvasElement.getBoundingClientRect();
      const current = transformRef.current;
      if (!current) return { x: screenX, y: screenY };

      // Account for centering wrapper - canvas transform layer's (0,0) is at center of container
      const x =
        (screenX - rect.left - rect.width / 2 - current.x) / current.zoom;
      const y =
        (screenY - rect.top - rect.height / 2 - current.y) / current.zoom;

      return { x, y };
    },
    [canvasRef, transformRef],
  );

  return {
    canvasToOverlay,
    overlayToCanvas,
    itemToCanvas,
    getItemsAtCoordinates,
    isCanvasAtClientCoordinates,
    clientToCanvas,
  };
};

// TODO: Rename this before merge, it is no longer simple lol
const SimpleCanvas = forwardRef(
  <T extends CanvasItem>(
    {
      items,
      children,
      onItemsChange,
      renderItem,
      onSelectionChange,
      onMouseEnter,
      onMouseLeave,
      onZoom,
      onMouseMove,
      onMouseDown,
      onMouseUp,
      onDrawRectStart,
      onDrawRectEnd,
      onDrop,
      className,
      forcePanOnClick,
      forceInteractableIfMouseOver,
      forceItemIdsInteractable,
      preventAllActions,
      preventSelect,
      onTransformChange,
      pageKey,
      onContextMenu,
      canDropDraggingItems,
      onDropItemIntoOtherItem,
      onDropItemOntoCanvas,
      onCancelDragItem,
      initialViewport,
    }: SimpleCanvasProps<T>,
    ref: React.Ref<SimpleCanvasRef>,
  ) => {
    // ================================================================================
    // BASIC STATE
    // ================================================================================

    // These state variables exist solely to trigger re-renders when they change
    const [isPanning, setIsPanning] = useState(false);
    const [isZooming, setIsZooming] = useState(false);
    const [isDraggingAndMoved, setIsDraggingAndMoved] = useState(false); // Dragging and moved a noticeable amount
    const [focusedId, setFocusedId] = useState<string | null>(null);
    const [isResizing, setIsResizing] = useState(false);

    // Clip bounds for temp storyboard during element drag
    const [dragClipBounds, setDragClipBounds] = useState<{
      x: number;
      y: number;
      width: number;
      height: number;
    } | null>(null);

    // Selection state
    const [selectionState, setSelectionState] = useState<SelectionState>({
      selectedIds: new Set(),
      isDragSelecting: false,
      dragStartX: 0,
      dragStartY: 0,
      dragEndX: 0,
      dragEndY: 0,
    });
    const selectionStateRef = useAutoUpdateRef(selectionState);

    const [mouseOverIds, setMouseOverIds] = useState<Set<string>>(new Set());
    const mouseOverIdsRef = useAutoUpdateRef(mouseOverIds);

    // Viewport visibility state
    const [viewportVisibility, setViewportVisibility] = useState<{
      visible: Set<string>;
      invisible: Set<string>;
    }>({
      visible: new Set(),
      invisible: new Set(),
    });
    const viewportVisibilityRef = useAutoUpdateRef(viewportVisibility);

    // ================================================================================
    // REFS
    // ================================================================================

    const canvasRef = useRef<HTMLDivElement | null>(null);
    const canvasTransformRef = useRef<HTMLDivElement | null>(null);
    const isDrawingRef = useRef(false);
    const hasInitializedViewportRef = useRef(false);
    const forcePanOnClickRef = useRef(forcePanOnClick);
    const isPanningRef = useRef(isPanning);
    const isZoomingRef = useRef(isZooming);
    const dragRafId = useRef<number | null>(null);
    const updateRafId = useRef<number | null>(null);
    const hasDraggedSinceMouseDownRef = useRef(false);
    const lockedPositionsRef = useRef<Set<string>>(new Set());
    const lockedSizesRef = useRef<Set<string>>(new Set());
    const itemIdToItemRef = useRef<Map<string, CanvasItemRef>>(new Map());
    const focusedIdRef = useRef<string | null>(null);
    const isDraggingAndMovedRef = useAutoUpdateRef(isDraggingAndMoved);

    const onDropItemIntoOtherItemRef = useAutoUpdateRef(
      onDropItemIntoOtherItem,
    );
    const onDropItemOntoCanvasRef = useAutoUpdateRef(onDropItemOntoCanvas);
    const onCancelDragItemRef = useAutoUpdateRef(onCancelDragItem);

    // Refs for items and DOM elements
    const itemsRef = useRef(items);
    const itemElementsRef = useRef<Map<string, HTMLDivElement>>(new Map());

    // Canvas state ref for panning
    const canvasStateRef = useRef({
      x: 0,
      y: 0,
      zoom: 1,
      panStartX: 0,
      panStartY: 0,
      panStartCanvasX: 0,
      panStartCanvasY: 0,
    });

    // Track how panning started and if it was temporarily suspended (e.g., cursor left screen)
    const panStartedWithMiddleRef = useRef(false);
    const panSuspendedRef = useRef(false);

    // Transform ref enables zero-render panning/zooming
    const transformRef = useRef({
      x: initialViewport?.x ?? 0,
      y: initialViewport?.y ?? 0,
      zoom: initialViewport?.zoom ?? 1,
    });

    // Gesture tracking for smooth trackpad zoom
    const gestureRef = useRef({
      isGesturing: false,
      lastEventTime: 0,
      accumulatedScale: 1,
      startTransform: {
        x: initialViewport?.x ?? 0,
        y: initialViewport?.y ?? 0,
        zoom: initialViewport?.zoom ?? 1,
      },
    });

    const dragStateRef = useRef<DragState>({
      isDragging: false,
      dragId: null,
      startX: 0,
      startY: 0,
      offsetX: 0,
      offsetY: 0,
      originalX: 0,
      originalY: 0,
      originalPositions: new Map(),
    });

    // Single accumulator for all pending updates
    const pendingUpdates = useRef({
      // Transform updates
      panDeltaX: 0,
      panDeltaY: 0,
      panAbsoluteX: null as number | null, // For absolute pan positioning (mouse drag)
      panAbsoluteY: null as number | null,
      zoomFactor: 1,
      zoomCenter: null as
        | { canvasX: number; canvasY: number }
        | { clientX: number; clientY: number }
        | null,
      gestureStartZoom: null as number | null, // Track the zoom level at gesture start
      transformNeedsUpdate: false,
      capturedPageKey: null as string | null, // Captures pageKey when transform starts
      // Drag updates
      dragPositions: new Map<string, { x: number; y: number }>(),
      dragNeedsUpdate: false,
      // Overlay updates
      itemVisibilityNeedsUpdate: new Set<string>(),
    });

    // Callback refs for performance
    const onZoomRef = useRef(onZoom);
    const onMouseMoveRef = useRef(onMouseMove);
    const onItemsChangeRef = useRef(onItemsChange);
    const parentItemsRef = useRef(items);
    const itemsToNotify = useRef<Set<string>>(new Set());
    const pageKeyRef = useRef(pageKey);

    // Auto-update refs for page viewport initialization (allows reading current
    // values in effects without adding them as dependencies)
    const itemsRefForViewport = useAutoUpdateRef(items);
    const onTransformChangeRef = useAutoUpdateRef(onTransformChange);

    const canDropDraggingItemsRef = useAutoUpdateRef(canDropDraggingItems);

    // ================================================================================
    // REF STATE UPDATES
    // ================================================================================

    useEffect(() => {
      forcePanOnClickRef.current = forcePanOnClick;
    }, [forcePanOnClick]);

    useEffect(() => {
      isPanningRef.current = isPanning;
    }, [isPanning]);

    useEffect(() => {
      isZoomingRef.current = isZooming;
    }, [isZooming]);

    useEffect(() => {
      onZoomRef.current = onZoom;
    }, [onZoom]);

    useEffect(() => {
      onMouseMoveRef.current = onMouseMove;
    }, [onMouseMove]);

    useEffect(() => {
      parentItemsRef.current = items;
      onItemsChangeRef.current = onItemsChange;
    }, [items, onItemsChange]);

    useEffect(() => {
      focusedIdRef.current = focusedId;
    }, [focusedId]);

    // ================================================================================
    // SYNC STATE TO SIMPLE CANVAS STORE
    // ================================================================================

    useEffect(() => {
      useSimpleCanvasStore.getState().setIsPanning(isPanning);
    }, [isPanning]);

    useEffect(() => {
      useSimpleCanvasStore.getState().setIsZooming(isZooming);
    }, [isZooming]);

    useEffect(() => {
      useSimpleCanvasStore.getState().setIsResizing(isResizing);
    }, [isResizing]);

    useEffect(() => {
      useSimpleCanvasStore.getState().setIsDragging(isDraggingAndMoved);
    }, [isDraggingAndMoved]);

    useEffect(() => {
      useSimpleCanvasStore
        .getState()
        .setIsDragSelecting(selectionState.isDragSelecting);
    }, [selectionState.isDragSelecting]);

    useEffect(() => {
      useSimpleCanvasStore.getState().setIsDrawing(isDrawingRef.current);
    }, [isResizing]); // isDrawing changes with resize for draw-rect

    // ================================================================================
    // CALLBACK HANDLERS
    // ================================================================================

    // Move these throttled/debounced callbacks here to avoid forward reference issues
    const onZoomThrottled = useCallback(
      throttle(
        (zoom: number) => {
          onZoomRef.current?.(zoom);
        },
        1000 / CALLBACK_THROTTLE_FPS,
        { leading: true, trailing: true },
      ),
      [],
    );

    const onMouseMoveThrottled = useCallback(
      throttle(
        (e: {
          clientX: number;
          clientY: number;
          canvasX: number;
          canvasY: number;
          draggingItemId?: string;
        }) => {
          onMouseMoveRef.current?.(e);
        },
        1000 / CALLBACK_THROTTLE_FPS,
        { leading: true, trailing: true },
      ),
      [],
    );

    const onTransformDebounced = useCallback(
      debounce(
        (
          transform: { x: number; y: number; zoom: number },
          pageKey?: string,
        ) => {
          onTransformChange?.(transform, pageKey);
        },
        500,
      ),
      [onTransformChange],
    );

    // When pageKey changes, flush any pending debounced transform calls
    // so they're saved to the correct (old) page before switching
    useEffect(() => {
      if (pageKeyRef.current !== pageKey && pageKeyRef.current !== undefined) {
        onTransformDebounced.flush();
      }
      pageKeyRef.current = pageKey;
    }, [pageKey, onTransformDebounced]);

    const updateViewportVisibilityThrottled = useCallback(
      throttle(
        () => {
          const canvasElement = canvasRef.current;
          if (!canvasElement) return;

          const { visible, invisible } = calculateViewportVisibility(
            itemsRef.current,
            // Buffer so we pre-load some items outside of the viewport
            canvasElement.clientWidth * 1.8,
            canvasElement.clientHeight * 1.8,
            transformRef.current,
          );

          // Only update state if visibility actually changed
          const currentVisibility = viewportVisibilityRef.current;
          const hasVisibleChanged =
            visible.size !== currentVisibility.visible.size ||
            Array.from(visible).some(
              (id) => !currentVisibility.visible.has(id),
            );
          const hasInvisibleChanged =
            invisible.size !== currentVisibility.invisible.size ||
            Array.from(invisible).some(
              (id) => !currentVisibility.invisible.has(id),
            );

          if (hasVisibleChanged || hasInvisibleChanged) {
            setViewportVisibility({
              visible,
              invisible,
            });
            viewportVisibilityRef.current = {
              visible,
              invisible,
            };
          }
        },
        1000 / 30, // 30fps throttle
        { leading: true, trailing: true },
      ),
      [viewportVisibilityRef],
    );

    const debouncedNotifyItems = useMemo(() => {
      return debounce((itemIds: string[], endOfAction: boolean) => {
        const updatedItems = itemIds
          .map((id) => {
            const currentItem = itemsRef.current.find((item) => item.id === id);
            const originalItem = parentItemsRef.current.find(
              (i) => i.id === id,
            );

            if (currentItem && originalItem) {
              return {
                ...originalItem,
                x: currentItem.x,
                y: currentItem.y,
                width: Math.round(currentItem.width),
                height: Math.round(currentItem.height),
              };
            }
            return null;
          })
          .filter((item) => item !== null) as T[];
        if (updatedItems.length > 0) {
          onItemsChangeRef.current?.(updatedItems, endOfAction);
        }
        itemsToNotify.current.clear();
      }, 1000);
    }, []);

    const notifyItemsChange = useCallback(
      (itemIds: string[], endOfAction: boolean) => {
        itemIds.forEach((itemId) => {
          itemsToNotify.current.add(itemId);
        });
        debouncedNotifyItems(Array.from(itemsToNotify.current), endOfAction);
      },
      [debouncedNotifyItems],
    );

    // ================================================================================
    // DERIVED VALUES
    // ================================================================================

    const {
      canvasToOverlay,
      overlayToCanvas,
      itemToCanvas,
      clientToCanvas,
      getItemsAtCoordinates,
      isCanvasAtClientCoordinates,
    } = useCanvasCoordinateHelpers(
      canvasRef,
      transformRef,
      itemsRef,
      itemElementsRef,
    );

    // ================================================================================
    // STATE TRACKING
    // ================================================================================

    // The only way we know that a pan/zoom via a wheel event has stopped is when
    // we stop getting wheel events, so we use debounced callbacks to track this

    const stopPanDebounced = useMemo(
      () =>
        debounce(() => {
          setIsPanning(false);
        }, 100),
      [],
    );

    const stopZoomDebounced = useMemo(
      () =>
        debounce(() => {
          setIsZooming(false);
          isZoomingRef.current = false;
        }, 100),
      [],
    );

    const setDragClipBoundsDebounced = useMemo(
      () =>
        debounce(
          (
            bounds: {
              x: number;
              y: number;
              width: number;
              height: number;
            } | null,
          ) => {
            setDragClipBounds(bounds);
          },
          150,
        ),
      [],
    );

    const setDragClipBoundsImmediate = useCallback(
      (
        bounds: { x: number; y: number; width: number; height: number } | null,
      ) => {
        setDragClipBoundsDebounced.cancel();
        setDragClipBounds(bounds);
      },
      [setDragClipBoundsDebounced],
    );

    // Cleanup debounced calls on unmount
    useEffect(() => {
      return () => {
        setDragClipBoundsDebounced.cancel();
      };
    }, [setDragClipBoundsDebounced]);

    const updateCanvasPanAbsolute = useCallback(
      (absoluteX: number, absoluteY: number) => {
        const pending = pendingUpdates.current;

        // Set absolute pan position (for mouse drag)
        pending.panAbsoluteX = absoluteX;
        pending.panAbsoluteY = absoluteY;
        pending.transformNeedsUpdate = true;

        if (!isPanningRef.current) {
          setIsPanning(true);
        }
      },
      [],
    );

    // ================================================================================
    // RESPONSES TO ITEM CHANGES FROM PROPS
    // ================================================================================

    // This is just to trigger re-renders when items passed in from props change
    const [_itemsToRender, setItemsToRender] = useState<T[]>(items);

    // Update items when props change
    useEffect(() => {
      const previousItemIds = new Set(itemsRef.current.map((item) => item.id));

      const updatedItems = items.map((item) => {
        const updatedItem = { ...item };
        const refItem = itemsRef.current.find((i) => i.id === item.id);

        // If the user is currently editing an item's position, don't override it,
        // otherwise we can treat it as if it was dragged there
        if (refItem && lockedPositionsRef.current.has(item.id)) {
          updatedItem.x = refItem.x;
          updatedItem.y = refItem.y;
        } else if (
          refItem &&
          (updatedItem.x !== refItem.x || updatedItem.y !== refItem.y)
        ) {
          pendingUpdates.current.dragPositions.set(item.id, {
            x: updatedItem.x,
            y: updatedItem.y,
          });
          pendingUpdates.current.dragNeedsUpdate = true;
        }

        // If the user is currently editing an item's size, don't override it,
        // otherwise we can treat it as if it was resized there
        if (refItem && lockedSizesRef.current.has(item.id)) {
          updatedItem.width = refItem.width;
          updatedItem.height = refItem.height;
        }

        if (updatedItem.hidden !== refItem?.hidden) {
          pendingUpdates.current.itemVisibilityNeedsUpdate.add(item.id);
        }

        // TODO: Clean this up
        if (!(updatedItem as any).created && refItem?.pending) {
          (updatedItem as any).drawing = true;
        }

        return updatedItem;
      });

      // If a pending item now has a real version from items array, remove it
      const pendingItems = itemsRef.current.filter((item) => {
        // Would normally have checked previousItems but for some reason when
        // createStoryboard is called its first added, then removed, then added
        // again, so we use this item.pending flag instead.
        return !items.some((i) => i.id === item.id) && item.pending;
      });

      itemsRef.current = updatedItems.concat(pendingItems);

      // Check if items were added or removed
      const newItemIds = new Set(itemsRef.current.map((item) => item.id));
      const itemsAdded = Array.from(newItemIds).some(
        (id) => !previousItemIds.has(id),
      );
      const itemsRemoved = Array.from(previousItemIds).some(
        (id) => !newItemIds.has(id),
      );

      // Force visibility recalculation if items were added or removed
      if (itemsAdded || itemsRemoved) {
        pendingUpdates.current.transformNeedsUpdate = true;
      }

      setItemsToRender(itemsRef.current);
      scheduleUpdate();
    }, [items]);

    // ================================================================================
    // MAIN CANVAS POSITIONING LOGIC
    // ================================================================================

    const getMaxZIndex = useCallback(() => {
      let maxZIndex = 0;
      itemElementsRef.current.forEach((element) => {
        const currentZIndex = parseInt(element.style.zIndex || "1", 10);
        maxZIndex = Math.max(maxZIndex, currentZIndex);
      });

      return maxZIndex;
    }, []);

    // applyPendingUpdates is the main render loop function that runs on every
    // frame (RAF) to apply all accumulated updates to the actual canvas DOM
    const applyPendingUpdates = useCallback(() => {
      const pending = pendingUpdates.current;

      // Apply transform updates
      if (pending.transformNeedsUpdate) {
        const current = transformRef.current;
        let newX = current.x;
        let newY = current.y;
        let newZoom = current.zoom;

        // Apply accumulated pan delta (for wheel events)
        if (pending.panDeltaX !== 0 || pending.panDeltaY !== 0) {
          newX += pending.panDeltaX;
          newY += pending.panDeltaY;
        }

        // Apply absolute pan positioning (for mouse drag)
        if (pending.panAbsoluteX !== null) {
          newX = pending.panAbsoluteX;
        }
        if (pending.panAbsoluteY !== null) {
          newY = pending.panAbsoluteY;
        }

        // Apply accumulated zoom with improved focal point preservation
        if (pending.zoomFactor !== 1 && pending.zoomCenter) {
          if (!isZoomingRef.current) {
            isZoomingRef.current = true;
            setIsZooming(true);
          }
          stopZoomDebounced();

          let focalX: number, focalY: number;

          if ("clientX" in pending.zoomCenter && canvasRef.current) {
            const rect = canvasRef.current.getBoundingClientRect();
            // Account for centering wrapper - zoom center is relative to container center
            focalX = pending.zoomCenter.clientX - rect.left - rect.width / 2;
            focalY = pending.zoomCenter.clientY - rect.top - rect.height / 2;
          } else if ("canvasX" in pending.zoomCenter) {
            // Already in canvas coordinates
            focalX = pending.zoomCenter.canvasX;
            focalY = pending.zoomCenter.canvasY;
          } else {
            // This case never happens its just to make TSC happy
            focalX = pending.zoomCenter.clientX;
            focalY = pending.zoomCenter.clientY;
          }

          // For gesture-based zoom, apply the scale factor directly
          // This gives us more precise control over the zoom behavior
          const targetZoom = pending.gestureStartZoom
            ? pending.gestureStartZoom * pending.zoomFactor
            : current.zoom * pending.zoomFactor;

          newZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, targetZoom));

          // Reset in case it got broken
          if (!newZoom) {
            newZoom = 1;
          }

          // Calculate the scale change ratio
          const scaleFactor = newZoom / current.zoom;

          // Apply the transform to keep the focal point fixed
          // This is the key formula: the focal point in screen space should remain constant
          // newTransform = focal - (focal - oldTransform) * scaleFactor
          newX = focalX - (focalX - newX) * scaleFactor;
          newY = focalY - (focalY - newY) * scaleFactor;
        }

        // Update transform ref
        transformRef.current = { x: newX, y: newY, zoom: newZoom };

        // Update canvas transform DOM
        const transformElement = canvasTransformRef.current;
        if (transformElement) {
          // The CSS variables do not update at _as_ smooth a framerate as
          // just directly setting raw px values to a transform is, so for the
          // overall canvas position we still set translate3d directly
          transformElement.style.transform = `translate3d(${newX}px, ${newY}px, 0) scale(${newZoom})`;
        }

        // Update panning state if needed
        if (pending.panDeltaX !== 0 || pending.panDeltaY !== 0) {
          if (!isPanningRef.current) {
            setIsPanning(true);
          }
          stopPanDebounced();
        }

        // Update css vars
        const canvasElement = canvasRef.current;
        if (canvasElement) {
          canvasElement.style.setProperty("--canvas-x", `${newX}px`);
          canvasElement.style.setProperty("--canvas-y", `${newY}px`);
          canvasElement.style.setProperty("--canvas-zoom", `${newZoom}`);
        }

        // Notify parent of zoom changes
        if (newZoom !== current.zoom) {
          onZoomThrottled(newZoom);
        }
        onTransformDebounced(
          { x: newX, y: newY, zoom: newZoom },
          pageKeyRef.current,
        );
      }

      // Apply drag updates
      if (pending.dragNeedsUpdate && pending.dragPositions.size > 0) {
        pending.dragPositions.forEach((pos, id) => {
          const element = itemElementsRef.current.get(id);
          if (element) {
            const item = itemsRef.current.find((i) => i.id === id);
            if (item) {
              item.x = pos.x;
              item.y = pos.y;
            }
            element.style.transform = `translate3d(${pos.x}px, ${pos.y}px, 0)`;
            const resizableOutlineRef =
              useSimpleCanvasStore.getState().itemToResizableOutlineRef[id];
            if (resizableOutlineRef) {
              resizableOutlineRef.instantUpdateWithRect(item!);
            }
          }
        });
        notifyItemsChange(Array.from(pending.dragPositions.keys()), false);
      }

      let maxZIndex = getMaxZIndex();
      let tempDraggingItem: T | undefined;
      for (const id of pending.itemVisibilityNeedsUpdate) {
        const item = itemsRef.current.find((i) => i.id === id);
        const element = itemElementsRef.current.get(id);
        if (item && element) {
          element.style.visibility = item.hidden ? "hidden" : "visible";

          if (!item.hidden && !element.style.zIndex) {
            // Set to the max z-index + 2
            const newZIndex = maxZIndex + 2;
            element.style.zIndex = newZIndex.toString();

            const resizableOutlineRef =
              useSimpleCanvasStore.getState().itemToResizableOutlineRef[id];
            if (resizableOutlineRef) {
              resizableOutlineRef.instantUpdateZIndex(maxZIndex + 1);
            }

            maxZIndex = newZIndex;
          }
        }

        if (id === TEMP_ELEMENT_DRAG_STORYBOARD_ID) {
          tempDraggingItem = item;
        }
      }

      // Always put it on top
      if (tempDraggingItem) {
        const element = itemElementsRef.current.get(tempDraggingItem.id);
        if (element) {
          element.style.zIndex = (maxZIndex + 2).toString();
        }
      }

      // Calculate viewport visibility whenever transform or positions change
      if (
        pending.transformNeedsUpdate ||
        pending.dragNeedsUpdate ||
        pending.itemVisibilityNeedsUpdate.size > 0
      ) {
        const canvasElement = canvasRef.current;
        if (canvasElement) {
          updateViewportVisibilityThrottled();
        }
      }

      // Reset all accumulators
      pending.panDeltaX = 0;
      pending.panDeltaY = 0;
      pending.panAbsoluteX = null;
      pending.panAbsoluteY = null;
      pending.zoomFactor = 1;
      pending.zoomCenter = null;
      pending.gestureStartZoom = null;
      pending.transformNeedsUpdate = false;
      pending.dragPositions.clear();
      pending.dragNeedsUpdate = false;

      updateRafId.current = null;
    }, [
      stopPanDebounced,
      notifyItemsChange,
      stopZoomDebounced,
      onZoomThrottled,
      onTransformDebounced,
      getMaxZIndex,
      updateViewportVisibilityThrottled,
    ]);

    // Schedule update if not already scheduled
    const scheduleUpdate = useCallback(() => {
      if (updateRafId.current) return; // Already scheduled

      updateRafId.current = requestAnimationFrame(applyPendingUpdates);
    }, [applyPendingUpdates]);

    // ================================================================================
    // PANINNG / ZOOMING HANDLERS
    // ================================================================================

    // Helper to detect if the wheel event is from a trackpad
    const isTrackpadEvent = useCallback((e: WheelEvent): boolean => {
      // More robust trackpad detection based on the values you're seeing

      const absDeltaY = Math.abs(e.deltaY);

      // Mouse wheel typically produces larger, more "stepped" values
      // Trackpad pinch produces smaller, more continuous values

      // If deltaY is very large (>80), it's likely mouse wheel
      if (absDeltaY > 80) {
        return false;
      }

      // If deltaY is very small (<15), it's likely trackpad
      if (absDeltaY < 15) {
        return true;
      }

      // For values in between (15-80), check if it has fine decimal precision
      // Mouse wheel often has specific fractional values like .000244140625 due to browser scaling
      // But trackpad usually has more varied, smaller decimals or is more "random"
      const decimalPart = absDeltaY - Math.floor(absDeltaY);
      const isSpecificMouseFraction =
        Math.abs(decimalPart - 0.000244140625) < 0.000001;

      if (isSpecificMouseFraction) {
        return false; // This specific fraction pattern indicates mouse wheel
      }

      // Default to trackpad for medium values with decimals
      return decimalPart > 0;
    }, []);

    // Wheel events can either mean panning/zooming in the canvas or panning/zooming from an iframe
    const handleWheelEvent = useCallback(
      (
        wheelEvent: WheelEvent,
        zoomCenter?:
          | { canvasX: number; canvasY: number }
          | { clientX: number; clientY: number },
      ) => {
        const pending = pendingUpdates.current;
        const gesture = gestureRef.current;
        const now = Date.now();

        if (
          (wheelEvent.ctrlKey || wheelEvent.metaKey) &&
          !isPanningRef.current
        ) {
          // Zoom handling with exponential scaling

          // Check if this is part of an ongoing gesture
          const isOngoingGesture =
            gesture.isGesturing &&
            now - gesture.lastEventTime < GESTURE_TIMEOUT_MS;

          if (!isOngoingGesture) {
            // Start new gesture
            gesture.isGesturing = true;
            gesture.accumulatedScale = 1;
            gesture.startTransform = { ...transformRef.current };
          }

          // Update gesture timing
          gesture.lastEventTime = now;

          // Detect input type and choose appropriate sensitivity
          const isTrackpad = isTrackpadEvent(wheelEvent);
          const sensitivity = isTrackpad
            ? TRACKPAD_ZOOM_SENSITIVITY
            : MOUSE_WHEEL_ZOOM_SENSITIVITY;

          // Apply meta key modifier
          const finalSensitivity =
            sensitivity * (wheelEvent.metaKey ? META_ZOOM_MULTIPLIER : 1);

          // Calculate scale using exponential function for smooth, continuous zoom
          // Negative deltaY = zoom in, positive = zoom out
          const scaleDelta = Math.exp(-wheelEvent.deltaY * finalSensitivity);

          // Accumulate scale for this gesture
          gesture.accumulatedScale *= scaleDelta;

          // Apply the accumulated scale from the gesture start
          pending.zoomFactor = gesture.accumulatedScale;
          pending.zoomCenter = zoomCenter || null;
          pending.gestureStartZoom = gesture.startTransform.zoom;
        } else {
          // Pan handling with increased sensitivity for more responsive feel
          const panSpeed = 1.5; // Increased from 1 for more responsive panning
          pending.panDeltaX -= wheelEvent.deltaX * panSpeed;
          pending.panDeltaY -= wheelEvent.deltaY * panSpeed;

          // Reset gesture state when panning
          gesture.isGesturing = false;
        }

        pending.transformNeedsUpdate = true;
        scheduleUpdate();
      },
      [scheduleUpdate, isTrackpadEvent],
    );

    // Clean up gesture state after timeout
    useEffect(() => {
      const checkGestureTimeout = () => {
        const gesture = gestureRef.current;
        const now = Date.now();

        if (
          gesture.isGesturing &&
          now - gesture.lastEventTime > GESTURE_TIMEOUT_MS
        ) {
          gesture.isGesturing = false;
          gesture.accumulatedScale = 1;
        }
      };

      const interval = setInterval(checkGestureTimeout, GESTURE_TIMEOUT_MS);
      return () => clearInterval(interval);
    }, []);

    // Wheel event handler
    useEffect(() => {
      const canvasElement = canvasRef.current;
      if (!canvasElement) return;

      const handleWheel = (e: WheelEvent) => {
        e.preventDefault();

        if (e.ctrlKey || e.metaKey) {
          // For zoom, pass clientX/clientY to be converted to canvas coordinates in RAF
          handleWheelEvent(e, { clientX: e.clientX, clientY: e.clientY });
        } else {
          // For pan, coordinates don't matter
          handleWheelEvent(e);
        }
      };

      canvasElement.addEventListener("wheel", handleWheel, { passive: false });
      return () => canvasElement.removeEventListener("wheel", handleWheel);
    }, [handleWheelEvent]);

    // Global mouse event listeners to maintain drag handling outside of the Canvas viewport
    useEffect(() => {
      const handleGlobalMouseMove = (e: MouseEvent) => {
        handleMouseMove(e as any);
      };

      const handleGlobalMouseUp = (e: MouseEvent) => {
        handleMouseUp(e);
      };

      window.addEventListener("mousemove", handleGlobalMouseMove);
      window.addEventListener("dragover", handleGlobalMouseMove);
      window.addEventListener("mouseup", handleGlobalMouseUp);

      return () => {
        window.removeEventListener("mousemove", handleGlobalMouseMove);
        window.removeEventListener("dragover", handleGlobalMouseMove);
        window.removeEventListener("mouseup", handleGlobalMouseUp);
      };
    }, []);

    // ================================================================================
    // SELECTION MANAGEMENT
    // ================================================================================

    // Debounced selection change callback to prevent excessive updates during drag selection/resize
    const onSelectionChangeDebounced = useMemo(
      () =>
        debounce((selectedIds: Set<string>) => {
          onSelectionChange?.(selectedIds);
        }, 200),
      [onSelectionChange],
    );

    const setSelectedIdsAndNotifyIfChanged = useCallback(
      (newSelectionState: SelectionState) => {
        setSelectionState(newSelectionState);

        const changed = !isEqual(
          selectionStateRef.current.selectedIds,
          newSelectionState.selectedIds,
        );
        if (changed) {
          onSelectionChangeDebounced(newSelectionState.selectedIds);
        }

        // Optimistically update the selection state ref
        selectionStateRef.current = newSelectionState;
      },
      [onSelectionChangeDebounced],
    );

    const selectItem = useCallback(
      (itemId: string, multiSelect: boolean = false) => {
        const newSelectionState = {
          ...selectionStateRef.current,
          selectedIds: new Set(
            multiSelect ? selectionStateRef.current.selectedIds : [],
          ),
        };

        if (newSelectionState.selectedIds.has(itemId)) {
          newSelectionState.selectedIds.delete(itemId);
        } else {
          newSelectionState.selectedIds.add(itemId);
        }

        setSelectedIdsAndNotifyIfChanged(newSelectionState);

        // Clear focus when selection changes (except for single select of the focused item)
        setFocusedId((prev) => {
          if (multiSelect || prev !== itemId) return null;
          return prev;
        });
      },
      [],
    );

    const clearSelection = useCallback(() => {
      setSelectedIdsAndNotifyIfChanged({
        ...selectionStateRef.current,
        selectedIds: new Set(),
      });
      setFocusedId(null);
    }, []);

    const startDragSelect = useCallback((canvasX: number, canvasY: number) => {
      selectionStateRef.current.isDragSelecting = true;
      setSelectedIdsAndNotifyIfChanged({
        ...selectionStateRef.current,
        isDragSelecting: true,
        dragStartX: canvasX,
        dragStartY: canvasY,
        dragEndX: canvasX,
        dragEndY: canvasY,
        selectedIds: new Set(),
      });
    }, []);

    const updateDragSelect = useCallback((canvasX: number, canvasY: number) => {
      const prev = selectionStateRef.current;

      const newSelectionState = {
        ...selectionStateRef.current,
      };
      // Calculate selection box
      const minX = Math.min(prev.dragStartX, canvasX);
      const maxX = Math.max(prev.dragStartX, canvasX);
      const minY = Math.min(prev.dragStartY, canvasY);
      const maxY = Math.max(prev.dragStartY, canvasY);

      // Find intersecting items
      const newSelectedIds = new Set<string>();
      itemsRef.current.forEach((item) => {
        const itemRight = item.x + item.width;
        const itemBottom = item.y + item.height;

        // Check if items intersect with selection box
        if (
          item.x < maxX &&
          itemRight > minX &&
          item.y < maxY &&
          itemBottom > minY
        ) {
          newSelectedIds.add(item.id);
        }
      });

      newSelectionState.dragEndX = canvasX;
      newSelectionState.dragEndY = canvasY;
      newSelectionState.selectedIds = newSelectedIds;

      setSelectedIdsAndNotifyIfChanged(newSelectionState);
    }, []);

    const endDragSelect = useCallback(() => {
      selectionStateRef.current.isDragSelecting = false;
      const newSelectionState = {
        ...selectionStateRef.current,
        isDragSelecting: false,
      };
      setSelectedIdsAndNotifyIfChanged(newSelectionState);
    }, []);

    // ================================================================================
    // Z-INDEX MANAGEMENT
    // ================================================================================

    const updateZIndicesForDrag = useCallback((idsToMove: Set<string>) => {
      // Find the highest current z-index among all items
      const maxZIndex = getMaxZIndex();

      if (idsToMove.size > 1) {
        // For multiple items, maintain their relative order but move all above others
        const itemsWithZIndex = Array.from(idsToMove)
          .map((id) => {
            const element = itemElementsRef.current.get(id);
            const currentZIndex = element
              ? parseInt(element.style.zIndex || "1", 10)
              : 1;
            return { id, currentZIndex };
          })
          .sort((a, b) => a.currentZIndex - b.currentZIndex);

        // Assign new z-indices starting from maxZIndex + 2, maintaining relative order
        // Keep 1 z index space between for outlines
        itemsWithZIndex.forEach(({ id }, index) => {
          const element = itemElementsRef.current.get(id);
          const newZIndex = maxZIndex + 2 + index * 2;
          if (element) {
            element.style.zIndex = newZIndex.toString();
          }

          const resizableOutlineRef =
            useSimpleCanvasStore.getState().itemToResizableOutlineRef[id];
          if (resizableOutlineRef) {
            resizableOutlineRef.instantUpdateZIndex(newZIndex + 1);
          }
        });
      } else {
        // Single item - just set to maxZIndex + 2
        const newZIndex = maxZIndex + 2;
        idsToMove.forEach((id) => {
          const element = itemElementsRef.current.get(id);
          // If the z index is the current max z index, don't update it
          if (element && parseInt(element.style.zIndex || "1") === maxZIndex) {
            return;
          }

          if (element) {
            element.style.zIndex = newZIndex.toString();
          }

          const resizableOutlineRef =
            useSimpleCanvasStore.getState().itemToResizableOutlineRef[id];
          if (resizableOutlineRef) {
            resizableOutlineRef.instantUpdateZIndex(newZIndex + 1);
          }
        });
      }
    }, []);

    // ================================================================================
    // EXTERNAL CONTROLS
    // ================================================================================

    // Zoom control functions
    const setZoom = useCallback(
      (zoom: number) => {
        const canvasElement = canvasRef.current;
        if (!canvasElement) return;

        const rect = canvasElement.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;

        const pending = pendingUpdates.current;
        const currentZoom = transformRef.current.zoom;
        const targetZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, zoom));

        pending.zoomFactor = targetZoom / currentZoom;

        // Center zoom around container center - this will be adjusted in applyPendingUpdates
        pending.zoomCenter = { clientX: centerX, clientY: centerY };
        pending.transformNeedsUpdate = true;

        scheduleUpdate();
      },
      [scheduleUpdate],
    );

    const getZoom = useCallback(() => {
      return transformRef.current.zoom;
    }, []);

    const centerViewOnItems = useCallback(
      (
        ids: string[],
        bufferFactor: number = 0.7,
        animate: boolean = false,
        skipZoom: boolean = false,
        minZoom: number = MIN_ZOOM,
      ) => {
        const items = itemsRef.current.filter((item) => ids.includes(item.id));
        const canvasElement = canvasRef.current;
        if (!canvasElement) return;

        // Pass minZoom to computeCenteredViewport so the position is calculated
        // correctly for the final bounded zoom value
        const viewport = computeCenteredViewport(
          items,
          canvasElement.clientWidth,
          canvasElement.clientHeight,
          bufferFactor,
          skipZoom ? transformRef.current.zoom : null,
          minZoom,
        );

        // Use the zoom from viewport directly - it's already bounded by minZoom
        const zoomToSet = viewport.zoom || transformRef.current.zoom || 1;

        if (animate) {
          const startTime = Date.now();
          const duration = 500;
          const startTransformX = transformRef.current.x;
          const startTransformY = transformRef.current.y;

          const interval = setInterval(() => {
            const timeElapsed = Date.now() - startTime;
            const percentComplete = Math.min(1, timeElapsed / duration);

            const newTransformX =
              startTransformX +
              (viewport.x - startTransformX) * percentComplete;
            const newTransformY =
              startTransformY +
              (viewport.y - startTransformY) * percentComplete;

            transformRef.current = {
              x: newTransformX,
              y: newTransformY,
              zoom: zoomToSet,
            };

            if (!skipZoom) {
              onZoomThrottled(transformRef.current.zoom);
            }
            pendingUpdates.current.transformNeedsUpdate = true;
            scheduleUpdate();

            if (percentComplete === 1) {
              clearInterval(interval);
            }
          }, 10);
          return;
        }

        transformRef.current = {
          x: viewport.x,
          y: viewport.y,
          zoom: zoomToSet,
        };
        if (!skipZoom) {
          onZoomThrottled(transformRef.current.zoom);
        }

        pendingUpdates.current.transformNeedsUpdate = true;
        scheduleUpdate();
      },
      [],
    );

    const setCanvasTransform = useCallback(
      (transform: { x: number; y: number; zoom: number }) => {
        transformRef.current = transform;
        pendingUpdates.current.transformNeedsUpdate = true;
        scheduleUpdate();
      },
      [scheduleUpdate],
    );

    // Initialize viewport when the selected page changes, either to the
    // previous viewport for that page or if its the first time viewing it then
    // a centered view of that page's storyboards
    useEffect(() => {
      if (pageKey === undefined) return;

      if (initialViewport) {
        // Restore saved viewport
        setCanvasTransform(initialViewport);
        // Notify parent to sync canvasStore - matches what the else branch does
        onTransformChangeRef.current?.(initialViewport, pageKey);
      } else {
        // No saved viewport - center on items or keep current position
        const canvasElement = canvasRef.current;
        const currentItems = itemsRefForViewport.current;

        if (canvasElement && currentItems.length > 0) {
          const centered = computeCenteredViewport(
            currentItems,
            canvasElement.clientWidth,
            canvasElement.clientHeight,
          );
          setCanvasTransform(centered);
          // Notify parent to save this viewport
          onTransformChangeRef.current?.(centered, pageKey);
        } else {
          // Empty page - save current transform so panning works correctly
          onTransformChangeRef.current?.({ ...transformRef.current }, pageKey);
        }
      }

      updateViewportVisibilityThrottled();
    }, [pageKey, setCanvasTransform, updateViewportVisibilityThrottled]);

    // Allows wheel events from iframes inside items to be forwarded to the canvas
    const onItemWheel = useCallback(
      (wheelEvent: WheelEvent, itemId: string) => {
        // These won't exist if its forwarded from an iframe
        wheelEvent.preventDefault?.();
        wheelEvent.stopPropagation?.();

        if (wheelEvent.ctrlKey || wheelEvent.metaKey) {
          // For iframe events, we need to convert coordinates from iframe space to canvas space
          // Find the item's position in canvas space to calculate proper zoom focal point
          const item = itemsRef.current.find((i) => i.id === itemId);
          if (!item) return;

          // Convert item position to screen coordinates for zoom focal point
          const transform = transformRef.current;
          const canvasX =
            (item.x + wheelEvent.x) * transform.zoom + transform.x;
          const canvasY =
            (item.y + wheelEvent.y) * transform.zoom + transform.y;

          handleWheelEvent(wheelEvent, { canvasX, canvasY });
        } else {
          handleWheelEvent(wheelEvent);
        }
      },
      [handleWheelEvent],
    );

    /**
     * Here we do not want to notify as the parent itself pushed this down
     */
    const setSelectedIds = useCallback((ids: Set<string>) => {
      const changed = !isEqual(selectionStateRef.current.selectedIds, ids);
      if (changed) {
        setSelectionState((prev) => ({ ...prev, selectedIds: ids }));
      }
    }, []);

    const imperativeSetFocusedId = useCallback((id: string | null) => {
      const changed = id !== focusedIdRef.current;
      if (changed) {
        setFocusedId(id);
      }
    }, []);

    // ================================================================================
    // CANVAS INITIAL POSITIONING
    // ================================================================================

    const canvasRefCallback = useCallback(
      (el: HTMLDivElement | null) => {
        if (el) {
          canvasRef.current = el;
          if (
            !hasInitializedViewportRef.current &&
            el.clientWidth > 0 &&
            el.clientHeight > 0
          ) {
            hasInitializedViewportRef.current = true;
            requestAnimationFrame(() => {
              const items = itemsRef.current.map((item) => item.id);
              if (items.length > 0) {
                centerViewOnItems(items, 0.7, false, false, MIN_ZOOM);
              }
            });
          }
        } else {
          canvasRef.current = null;
        }
      },
      [centerViewOnItems],
    );

    // Cleanup RAF on unmount
    useEffect(() => {
      return () => {
        if (dragRafId.current) {
          cancelAnimationFrame(dragRafId.current);
        }
        if (updateRafId.current) {
          cancelAnimationFrame(updateRafId.current);
        }
      };
    }, []);

    // ================================================================================
    // DRAGGING & RESIZING
    // ================================================================================

    const startDragItem = useCallback(
      (item: T, { canvasX, canvasY }: { canvasX: number; canvasY: number }) => {
        dragStateRef.current = {
          isDragging: true,
          dragId: item.id,
          startX: canvasX,
          startY: canvasY,
          offsetX: canvasX - item.x,
          offsetY: canvasY - item.y,
          originalX: item.x,
          originalY: item.y,
          originalPositions: new Map(),
        };

        // Determine which items will be moved
        const selection = selectionStateRef.current;
        if (!selection.selectedIds.has(item.id)) {
          selectItem(item.id, false);
        }

        const idsToMove = selection.selectedIds;

        for (const id of idsToMove) {
          lockedPositionsRef.current.add(id);
        }

        // Update z-indices once at the start of drag
        updateZIndicesForDrag(idsToMove);

        // Store original positions for all items that will be moved
        const originalPositions = new Map<string, { x: number; y: number }>();
        idsToMove.forEach((id) => {
          const itemToMove = itemsRef.current.find((r) => r.id === id);
          if (itemToMove) {
            originalPositions.set(id, { x: itemToMove.x, y: itemToMove.y });
          }
        });

        // Update drag state with original positions
        dragStateRef.current.originalPositions = originalPositions;

        // When dragging items that are inside a storyboard we want them to
        // initially appear to be clipped by the storyboard they're in
        if (item.id === TEMP_ELEMENT_DRAG_STORYBOARD_ID) {
          const { topItem: itemUnderMouse } = getItemsAtCoordinates(
            canvasX,
            canvasY,
            [item.id],
          );
          if (itemUnderMouse) {
            const hoveredItem = itemsRef.current.find(
              (i) => i.id === itemUnderMouse.id,
            );
            if (hoveredItem) {
              setDragClipBoundsImmediate({
                x: hoveredItem.x,
                y: hoveredItem.y,
                width: hoveredItem.width,
                height: hoveredItem.height,
              });
            }
          }
        }
      },
      [
        selectItem,
        updateZIndicesForDrag,
        getItemsAtCoordinates,
        setDragClipBoundsImmediate,
      ],
    );

    const endDragItem = useCallback(
      ({ canvasX, canvasY }: { canvasX: number; canvasY: number }) => {
        if (!dragStateRef.current.dragId) return;
        for (const id of dragStateRef.current.originalPositions.keys()) {
          lockedPositionsRef.current.delete(id);
        }
        const droppingItemId = dragStateRef.current.dragId;
        for (const id of dragStateRef.current.originalPositions.keys()) {
          notifyItemsChange([id], true);
        }

        const { topItem: itemAtCoordinates } = getItemsAtCoordinates(
          canvasX,
          canvasY,
          [droppingItemId],
        );
        if (!itemAtCoordinates) {
          // Always call element drag end handler for element drags
          onDropItemOntoCanvasRef.current?.(droppingItemId);
        } else if (
          canDropDraggingItemsRef.current &&
          droppingItemId &&
          isDraggingAndMovedRef.current
        ) {
          // Only call drop into other item for regular storyboard drags when enabled
          onDropItemIntoOtherItemRef.current?.(
            droppingItemId,
            itemAtCoordinates.id,
            canvasX - itemAtCoordinates.x,
            canvasY - itemAtCoordinates.y,
          );
        } else {
          onCancelDragItemRef.current?.();
        }

        // Clear clip bounds if this was a temp storyboard drag
        if (droppingItemId === TEMP_ELEMENT_DRAG_STORYBOARD_ID) {
          setDragClipBoundsImmediate(null);
        }

        dragStateRef.current = {
          isDragging: false,
          dragId: null,
          startX: 0,
          startY: 0,
          offsetX: 0,
          offsetY: 0,
          originalX: 0,
          originalY: 0,
          originalPositions: new Map(),
        };

        setIsDraggingAndMoved(false);
        isDraggingAndMovedRef.current = false;
      },
      [
        getItemsAtCoordinates,
        canDropDraggingItemsRef,
        isDraggingAndMovedRef,
        notifyItemsChange,
        onDropItemOntoCanvasRef,
        onDropItemIntoOtherItemRef,
        setDragClipBoundsImmediate,
      ],
    );

    const onResizeStart = useCallback(
      (item: T) => {
        setIsResizing(true);
        lockedSizesRef.current.add(item.id);
        lockedPositionsRef.current.add(item.id);
      },
      [setIsResizing],
    );

    const handleResizeEnd = useCallback(
      (newItem: T) => {
        // Update internal state
        itemsRef.current = itemsRef.current.map((i) =>
          i.id === newItem.id
            ? {
                ...i,
                ...pick(newItem, ["x", "y", "width", "height"]),
              }
            : i,
        );

        setIsResizing(false);
        notifyItemsChange([newItem.id], true);
        lockedSizesRef.current.delete(newItem.id);
        lockedPositionsRef.current.delete(newItem.id);

        if (isDrawingRef.current) {
          onDrawRectEnd?.(
            itemsRef.current.find((r) => r.id === newItem.id) || newItem,
          );
          isDrawingRef.current = false;
        }
        debouncedNotifyItems.flush();
      },
      [notifyItemsChange, setIsResizing, onDrawRectEnd],
    );

    const imperativeStartDragItem = useCallback(
      (item: T, { canvasX, canvasY }: { canvasX: number; canvasY: number }) => {
        startDragItem(item, { canvasX, canvasY });

        // Immediately indicate that we've moved
        setIsDraggingAndMoved(true);
        isDraggingAndMovedRef.current = true;
      },
      [startDragItem, setIsDraggingAndMoved, isDraggingAndMovedRef],
    );

    // ================================================================================
    // IMPERATIVE HANDLE
    // ================================================================================

    const getTransform = useCallback(() => {
      return { ...transformRef.current };
    }, []);

    useImperativeHandle(
      ref,
      () => ({
        onItemWheel,
        setZoom,
        getZoom,
        getTransform,
        getCanvasRef: () => canvasRef.current,
        itemToCanvas,
        setFocusedId: imperativeSetFocusedId,
        setSelectedIds,
        centerViewOnItems,
        setCanvasTransform,
        overlayToCanvas,
        clientToCanvas,
        canvasToOverlay,
        getItemsAtCoordinates,
        startDragItem: imperativeStartDragItem,
        isCanvasAtClientCoordinates,
        getIsDragging: () => dragStateRef.current.isDragging,
      }),
      [
        onItemWheel,
        setZoom,
        getZoom,
        getTransform,
        itemToCanvas,
        imperativeSetFocusedId,
        setSelectedIds,
        centerViewOnItems,
        setCanvasTransform,
        overlayToCanvas,
        clientToCanvas,
        canvasToOverlay,
        getItemsAtCoordinates,
        imperativeStartDragItem,
        isCanvasAtClientCoordinates,
      ],
    );

    // ================================================================================
    // MOUSE & KEYBOARD EVENT HANDLERS
    // ================================================================================

    const handleMouseDown = useCallback(
      (e: React.MouseEvent, itemId?: string) => {
        // e.preventDefault();
        e.stopPropagation();

        const isLeftButton = e.button === 0;
        const isMiddleButton = e.button === 1;

        // Middle mouse always pans the canvas
        if (isMiddleButton) {
          e.preventDefault(); // prevent browser auto-scroll
          setIsPanning(true);
          canvasStateRef.current.panStartX = e.clientX;
          canvasStateRef.current.panStartY = e.clientY;
          canvasStateRef.current.panStartCanvasX = transformRef.current.x;
          canvasStateRef.current.panStartCanvasY = transformRef.current.y;
          panStartedWithMiddleRef.current = true;
          panSuspendedRef.current = false;
          return;
        }

        const item = itemsRef.current.find((r) => r.id === itemId);
        // Convert screen coordinates to canvas coordinates
        const { x: canvasX, y: canvasY } = clientToCanvas(e.clientX, e.clientY);

        // Only notify parent for primary button to avoid selection changes on aux clicks
        if (isLeftButton) {
          onMouseDown?.({
            clientX: e.clientX,
            clientY: e.clientY,
            canvasX,
            canvasY,
            itemId,
            item,
          });
        }

        // Canvas click
        if (forcePanOnClickRef.current) {
          // Start panning
          setIsPanning(true);
          canvasStateRef.current.panStartX = e.clientX;
          canvasStateRef.current.panStartY = e.clientY;
          canvasStateRef.current.panStartCanvasX = transformRef.current.x;
          canvasStateRef.current.panStartCanvasY = transformRef.current.y;
          panStartedWithMiddleRef.current = false;
          panSuspendedRef.current = false;
          return;
        }

        if (preventAllActions || isPanningRef.current) {
          return;
        }

        if (!itemId) {
          if (isLeftButton) {
            setFocusedId(null);

            // Start drag select
            if (!e.shiftKey) {
              clearSelection();

              const newItem = onDrawRectStart?.({ x: canvasX, y: canvasY });
              if (newItem === true) {
                return;
              }
              if (newItem) {
                isDrawingRef.current = true;
                itemsRef.current.push({ ...newItem, pending: true });
                selectItem(newItem.id, false);
                requestAnimationFrame(() => {
                  const resizableOutlineRef =
                    useSimpleCanvasStore.getState().itemToResizableOutlineRef[
                      newItem.id
                    ];
                  if (resizableOutlineRef) {
                    resizableOutlineRef.startResize(e, "se");
                  }
                });
                // Add new item to visible items
                const newViewportVisibility = {
                  visible: new Set(viewportVisibilityRef.current.visible).add(
                    newItem.id,
                  ),
                  invisible: new Set(viewportVisibilityRef.current.invisible),
                };
                setViewportVisibility(newViewportVisibility);
                viewportVisibilityRef.current = newViewportVisibility;
                return;
              }
            }
            startDragSelect(canvasX, canvasY);
          }
          return;
        }

        if (!item || !isLeftButton || preventSelect) {
          return;
        }

        // Reset drag tracking for this mouse down
        hasDraggedSinceMouseDownRef.current = false;

        // If meta key is clicked, ignore the click as it should pass through to the iframe
        if (e.metaKey) return;

        // If an item is focsed but not selected - something inside the item is selected
        // Therefore, do not start a drag or adjust selection
        const focusedButNotSelected =
          focusedId === itemId &&
          !selectionStateRef.current.selectedIds.has(itemId);
        if (focusedButNotSelected) {
          return;
        }

        // Handle item selection
        const currentSelection = selectionStateRef.current;
        if (e.shiftKey) {
          // Multi-select mode
          selectItem(itemId, true);
        } else if (!currentSelection.selectedIds.has(itemId)) {
          // Single select mode - only if item not already selected
          selectItem(itemId, false);
        }

        // Start dragging the clicked item
        startDragItem(item, { canvasX, canvasY });

        // If item is already selected (part of multi-selection), don't change selection yet
        // We'll handle it in mouseup if we didn't drag
      },
      [
        clearSelection,
        startDragSelect,
        startDragItem,
        selectItem,
        clientToCanvas,
        onMouseDown,
        preventAllActions,
        preventSelect,
        focusedId,
        onDrawRectStart,
      ],
    );

    const handleMouseMove = useCallback(
      (e: React.MouseEvent) => {
        // Convert screen coordinates to canvas coordinates
        const { x: canvasX, y: canvasY } = clientToCanvas(e.clientX, e.clientY);

        const itemsAtPoint = getItemsAtCoordinates(canvasX, canvasY);
        const newMouseOverIds = new Set(
          itemsAtPoint.items?.map((item) => item.id) ?? [],
        );
        if (!isEqual(mouseOverIdsRef.current, newMouseOverIds)) {
          mouseOverIdsRef.current = newMouseOverIds;
          setMouseOverIds(newMouseOverIds);
        }

        // If there were any pending items, just delete them
        if (itemsRef.current?.some((item) => item.pending) && e.buttons === 0) {
          itemsRef.current = itemsRef.current.filter((item) => !item.pending);
        }

        // Ensure we always release even if we missed the mouseup event
        if (
          (dragStateRef.current.isDragging ||
            selectionStateRef.current.isDragSelecting) &&
          e.buttons === 0
        ) {
          handleMouseUp(e);
          return;
        }
        if (isPanningRef.current && e.buttons === 0) {
          // If panning started with middle mouse and we re-entered with no buttons,
          // don't auto-end here. We'll resolve on mouseenter based on buttons state.
          if (panStartedWithMiddleRef.current) {
            return;
          }
          panSuspendedRef.current = true;
          return;
        }

        const currentCanvasState = canvasStateRef.current;
        const currentSelection = selectionStateRef.current;

        // Handle panning (spacebar/hand tool via forcePan, or middle mouse)
        if (isPanningRef.current) {
          const deltaX = e.clientX - currentCanvasState.panStartX;
          const deltaY = e.clientY - currentCanvasState.panStartY;

          const targetX = currentCanvasState.panStartCanvasX + deltaX;
          const targetY = currentCanvasState.panStartCanvasY + deltaY;

          // Use absolute positioning for mouse drag panning
          updateCanvasPanAbsolute(targetX, targetY);
          scheduleUpdate();
          return;
        }

        onMouseMoveThrottled({
          clientX: e.clientX,
          clientY: e.clientY,
          canvasX,
          canvasY,
          draggingItemId: dragStateRef.current.dragId ?? undefined,
        });

        // Handle drag-to-select
        if (currentSelection.isDragSelecting) {
          updateDragSelect(canvasX, canvasY);
          return;
        }

        // Handle item dragging
        const dragState = dragStateRef.current;
        if (!dragState.isDragging || !dragState.dragId) {
          return;
        }

        const newX = canvasX - dragState.offsetX;
        const newY = canvasY - dragState.offsetY;

        // Calculate movement delta
        const deltaX = newX - dragState.originalX;
        const deltaY = newY - dragState.originalY;

        if (Math.abs(deltaX) > 5 || Math.abs(deltaY) > 5) {
          setIsDraggingAndMoved(true);
          isDraggingAndMovedRef.current = true;
        }

        // Mark that we've dragged since mouse down
        if (
          !hasDraggedSinceMouseDownRef.current &&
          (Math.abs(deltaX) > 5 || Math.abs(deltaY) > 5)
        ) {
          hasDraggedSinceMouseDownRef.current = true;
        }

        // Get items to move
        const selection = selectionStateRef.current;
        const idsToMove = selection.selectedIds.has(dragState.dragId)
          ? selection.selectedIds
          : new Set([dragState.dragId]);

        // Update data immediately to keep visual and data in sync
        idsToMove.forEach((id) => {
          const itemIndex = itemsRef.current.findIndex((r) => r.id === id);
          if (itemIndex !== -1) {
            const originalItem = itemsRef.current[itemIndex];
            let originalPos = dragState.originalPositions.get(id);

            // Item wasn't available, set the original position now
            if (!originalPos) {
              originalPos = {
                x: originalItem.x,
                y: originalItem.y,
              };
              dragState.originalPositions.set(id, originalPos);
            }

            const updatedItem = {
              ...originalItem,
              x: originalPos.x + deltaX,
              y: originalPos.y + deltaY,
            };

            itemsRef.current[itemIndex] = updatedItem;

            // Accumulate drag position for RAF'd DOM update
            pendingUpdates.current.dragPositions.set(id, {
              x: updatedItem.x,
              y: updatedItem.y,
            });
          }
        });

        // Handle clipping for temp storyboard during element drag
        if (dragState.dragId === TEMP_ELEMENT_DRAG_STORYBOARD_ID) {
          // Find which item we're over (excluding the temp storyboard itself)
          const { topItem: itemUnderMouse } = getItemsAtCoordinates(
            canvasX,
            canvasY,
            [dragState.dragId],
          );

          if (itemUnderMouse) {
            // We're over another storyboard - enable clipping
            const hoveredItemRef = itemsRef.current.find(
              (i) => i.id === itemUnderMouse.id,
            );

            if (hoveredItemRef) {
              const currentBounds = dragClipBounds;
              const newBounds = {
                x: hoveredItemRef.x,
                y: hoveredItemRef.y,
                width: hoveredItemRef.width,
                height: hoveredItemRef.height,
              };

              // Check if bounds actually changed
              const boundsChanged =
                !currentBounds ||
                currentBounds.x !== newBounds.x ||
                currentBounds.y !== newBounds.y ||
                currentBounds.width !== newBounds.width ||
                currentBounds.height !== newBounds.height;

              if (boundsChanged) {
                if (!currentBounds) {
                  setDragClipBoundsDebounced(newBounds);
                } else {
                  setDragClipBoundsImmediate(newBounds);
                }
              }
            }
          } else {
            setDragClipBoundsImmediate(null);
          }
        }

        // Schedule unified updates
        pendingUpdates.current.dragNeedsUpdate = true;
        scheduleUpdate();
      },
      [
        updateDragSelect,
        clientToCanvas,
        updateCanvasPanAbsolute,
        scheduleUpdate,
        onMouseMoveThrottled,
        notifyItemsChange,
        getItemsAtCoordinates,
        setDragClipBoundsDebounced,
        setDragClipBoundsImmediate,
        dragClipBounds,
      ],
    );

    const handleMouseUp = useCallback(
      (e: React.MouseEvent<Element, MouseEvent> | MouseEvent) => {
        onMouseUp?.(e);
        debouncedNotifyItems.flush(); // Clear any pending debounced calls

        // End panning
        if (isPanningRef.current) {
          setIsPanning(false);
          panSuspendedRef.current = false;
          panStartedWithMiddleRef.current = false;
        }

        // Cancel any pending drag RAF
        if (dragRafId.current) {
          cancelAnimationFrame(dragRafId.current);
          dragRafId.current = null;
        }

        // Apply any final pending updates immediately to ensure final positions are set
        applyPendingUpdates();

        // End drag-to-select
        const currentSelection = selectionStateRef.current;
        if (currentSelection.isDragSelecting) {
          endDragSelect();
          return;
        }

        // End item dragging
        const dragState = dragStateRef.current;
        if (!dragState.isDragging || !dragState.dragId) return;

        // Get items that were moved
        const selection = selectionStateRef.current;
        const idsToMove = selection.selectedIds.has(dragState.dragId)
          ? selection.selectedIds
          : new Set([dragState.dragId]);

        // Keep z-index permanently - don't reset after drag ends

        // Update React state with final positions
        for (const id of idsToMove) {
          const updatedItem = itemsRef.current.find((r) => r.id === id);
          if (updatedItem) {
            notifyItemsChange([updatedItem.id], true);
          }
        }

        // Final item change notifications - flush any pending, then notify each item
        debouncedNotifyItems.flush(); // Clear any pending debounced calls first

        const { x: canvasX, y: canvasY } = clientToCanvas(e.clientX, e.clientY);

        // Reset drag state
        endDragItem({ canvasX, canvasY });
      },
      [endDragSelect, notifyItemsChange, applyPendingUpdates, selectItem],
    );

    // Prevent global text selection/drag while panning the canvas
    useEffect(() => {
      if (!isPanning) return;
      const preventDefault = (e: Event) => e.preventDefault();
      document.addEventListener("selectstart", preventDefault, {
        capture: true,
      });
      document.addEventListener("dragstart", preventDefault, { capture: true });
      return () => {
        document.removeEventListener("selectstart", preventDefault, true);
        document.removeEventListener("dragstart", preventDefault, true);
      };
    }, [isPanning]);

    // ================================================================================
    // RENDER
    // ================================================================================

    const dragSelectRectStyles = useMemo(() => {
      const { dragStartX, dragStartY, dragEndX, dragEndY } = selectionState;
      const { x: startX, y: startY } = canvasToOverlay(dragStartX, dragStartY);
      const { x: endX, y: endY } = canvasToOverlay(dragEndX, dragEndY);

      return {
        left: Math.min(startX, endX),
        top: Math.min(startY, endY),
        width: Math.abs(endX - startX),
        height: Math.abs(endY - startY),
        zIndex: 1500,
      };
    }, [selectionState]);

    return (
      <div
        key="canvas-container"
        ref={canvasRefCallback}
        data-testid="simple-canvas-container"
        data-canvas-container
        className={cn(
          `relative w-full h-full bg-secondary-200 overflow-hidden ${
            isPanningRef.current ? "cursor-grabbing" : ""
          }`,
          className,
        )}
        style={
          {
            touchAction: "none", // Prevent default touch behaviors
            overscrollBehavior: "contain",
            "--canvas-x": `${canvasStateRef.current.x}px`,
            "--canvas-y": `${canvasStateRef.current.y}px`,
            "--canvas-zoom": `${canvasStateRef.current.zoom}`,
          } as CssPropertiesWithVars
        }
        onMouseDown={(e) => handleMouseDown(e)}
        onMouseLeave={() => {
          if (isPanningRef.current && panStartedWithMiddleRef.current) {
            panSuspendedRef.current = true;
          }
        }}
        onMouseEnter={(e) => {
          if (panSuspendedRef.current && panStartedWithMiddleRef.current) {
            // If middle button still held, resume by resetting pan anchors
            const middleHeld = (e.buttons & 4) !== 0;
            if (middleHeld) {
              canvasStateRef.current.panStartX = e.clientX;
              canvasStateRef.current.panStartY = e.clientY;
              canvasStateRef.current.panStartCanvasX = transformRef.current.x;
              canvasStateRef.current.panStartCanvasY = transformRef.current.y;
              panSuspendedRef.current = false;
              // keep isPanningRef/current true
            } else {
              // Middle not held anymore – end panning gracefully
              setIsPanning(false);
              panSuspendedRef.current = false;
              panStartedWithMiddleRef.current = false;
            }
          }
        }}
        onDragOver={(e) => {
          e.preventDefault();

          // Update the mouse position in the parent too
          const { x: canvasX, y: canvasY } = clientToCanvas(
            e.clientX,
            e.clientY,
          );
          onMouseMoveThrottled({
            clientX: e.clientX,
            clientY: e.clientY,
            canvasX,
            canvasY,
            draggingItemId: dragStateRef.current.dragId ?? undefined,
          });
        }}
        onContextMenu={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onContextMenu?.({ clientX: e.clientX, clientY: e.clientY });
        }}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onDrop?.(e);
        }}
      >
        {/* Centering wrapper to keep canvas content centered in container */}
        <div
          className="absolute"
          style={{
            left: "50%",
            top: "50%",
            transform: "translate3d(-50%, -50%, 0)",
          }}
        >
          {/* GPU-accelerated canvas content layer */}
          <div
            ref={canvasTransformRef}
            style={{
              transform: `translate3d(var(--canvas-x), var(--canvas-y), 0) scale(var(--canvas-zoom))`,
              transformOrigin: "0 0",
              willChange: isPanning || isZooming ? "transform" : "auto", // Uses too much memory if always enabled
            }}
          >
            {/* Items */}
            {itemsRef.current.map((item) => {
              const isSelected = selectionStateRef.current.selectedIds.has(
                item.id,
              );
              const isFocused = focusedId === item.id;

              const numDraggingItems =
                selectionStateRef.current.selectedIds.size;

              const draggingThisItem =
                isDraggingAndMovedRef.current && isSelected;
              const canDropThisItem = Boolean(
                canDropDraggingItems &&
                  draggingThisItem &&
                  numDraggingItems === 1,
              );

              const forceInteractable =
                forceInteractableIfMouseOver &&
                mouseOverIdsRef.current.has(item.id);

              const isInteractable = Boolean(
                (isFocused ||
                  forceInteractable ||
                  forceItemIdsInteractable?.has(item.id) ||
                  (isDraggingAndMoved && numDraggingItems === 1)) &&
                  !isPanning &&
                  !isZooming &&
                  !isResizing &&
                  !forcePanOnClick &&
                  !draggingThisItem,
              );

              const isDraggingItem = isSelected && isDraggingAndMoved;

              // Only clip the temp storyboard item when dragging over another storyboard
              const isTempStoryboard =
                item.id === TEMP_ELEMENT_DRAG_STORYBOARD_ID;
              const clipBounds = isTempStoryboard ? dragClipBounds : null;

              return (
                <Fragment key={item.id}>
                  <ClipBounds bounds={clipBounds}>
                    <div
                      id="simple-canvas-item-container"
                      data-testid={`canvas-item-${item.id}`}
                      data-selected={isSelected}
                      key={item.id}
                      ref={(el) => {
                        if (el) {
                          const newItem = !itemElementsRef.current.get(item.id);
                          itemElementsRef.current.set(item.id, el);

                          // Set the z index for this item if it only just rendered
                          if (
                            newItem &&
                            dragStateRef.current.dragId === item.id
                          ) {
                            updateZIndicesForDrag(new Set([item.id]));
                          }
                        } else {
                          itemElementsRef.current.delete(item.id);
                        }
                      }}
                      className="absolute select-none transition-none"
                      style={{
                        left: 0,
                        top: 0,
                        width: item.width,
                        height: item.height,
                        transform: `translate3d(${item.x}px, ${item.y}px, 0)`,
                        transformOrigin: "0 0",
                        willChange:
                          isPanning || isZooming || isDraggingItem || isResizing
                            ? "transform"
                            : "auto", // Uses too much memory if always enabled
                        visibility: item.hidden ? "hidden" : "visible",

                        // Allow pointer events through so that you can drop one item into another
                        // For now this is only allowed for single item dragging
                        pointerEvents:
                          draggingThisItem && numDraggingItems === 1
                            ? "none"
                            : "auto",
                        // contain: "layout style paint size",
                      }}
                      onMouseDown={(e) => handleMouseDown(e, item.id)}
                      onMouseEnter={(e) => {
                        onMouseEnter?.(e, item.id);
                      }}
                      onMouseLeave={(e) => {
                        onMouseLeave?.(e, item.id);
                      }}
                      onContextMenu={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onContextMenu?.(
                          { clientX: e.clientX, clientY: e.clientY },
                          item.id,
                        );
                      }}
                    >
                      {/* Item content container - pointer events controlled by overlay state */}
                      <div
                        className="w-full h-full relative"
                        style={{
                          pointerEvents: isInteractable ? "auto" : "none",
                        }}
                      >
                        {renderItem(
                          item,
                          {
                            isSelected,
                            isFocused,
                            isResizing,
                            isPanning,
                            isZooming,
                            isInteractable,

                            canDropThisItem,
                            draggingThisItem,
                            numDraggingItems: isDraggingAndMoved
                              ? numDraggingItems
                              : 0,

                            isVisible:
                              viewportVisibilityRef.current.visible.has(
                                item.id,
                              ),
                          },
                          (ref) => {
                            if (ref) {
                              itemIdToItemRef.current.set(item.id, ref);
                            } else {
                              itemIdToItemRef.current.delete(item.id);
                            }
                          },
                        )}
                      </div>
                    </div>
                  </ClipBounds>
                  <ResizableOutline
                    ref={(el) => {
                      if (el) {
                        useSimpleCanvasStore
                          .getState()
                          .setItemToResizableOutlineRef(item.id, el);
                      } else {
                        useSimpleCanvasStore
                          .getState()
                          .setItemToResizableOutlineRef(item.id, null);
                      }
                    }}
                    key={`selected-${item.id}`}
                    itemRect={item}
                    elementsResizing={
                      [
                        // The element in the canvas itself
                        itemElementsRef.current.get(item.id),

                        // Notify the simple canvas item that it also should be resized
                        itemIdToItemRef.current.get(item.id),
                      ].filter(Boolean) as (HTMLElement | CanvasItemRef)[]
                    }
                    onItemChange={(newBounds) => {
                      // Update internal state
                      itemsRef.current = itemsRef.current.map((i) =>
                        i.id === item.id
                          ? {
                              ...i,
                              ...pick(newBounds, ["x", "y", "width", "height"]),
                            }
                          : i,
                      );
                      notifyItemsChange([item.id], false);
                    }}
                    onResizeStart={() => onResizeStart(item)}
                    onResizeEnd={(newBounds) =>
                      handleResizeEnd({
                        ...item,
                        ...pick(newBounds, ["x", "y", "width", "height"]),
                      })
                    }
                    clientToCanvas={clientToCanvas}
                    canMove={true}
                    hidden={
                      !isSelected ||
                      item.hidden ||
                      item.id === TEMP_ELEMENT_DRAG_STORYBOARD_ID
                    }
                    notInteractable={isPanning || forcePanOnClick}
                    droppingUI={canDropThisItem}
                  />
                </Fragment>
              );
            })}
            {children}
          </div>

          {/* Drag-to-select rectangle - rendered in overlay coordinates for consistent border size */}
          {selectionStateRef.current.isDragSelecting && (
            <div
              className="absolute border border-blue-400 bg-blue-100/20 pointer-events-none"
              style={dragSelectRectStyles}
            />
          )}
        </div>
      </div>
    );
  },
);

SimpleCanvas.displayName = "SimpleCanvas";

export default SimpleCanvas;
export type { SimpleCanvasProps };
